/**
 * Pure H OS · Sprint 2 · Global Settings page.
 *
 * Company · Workspace · Appearance · AI · Security.
 * Composes Card, SectionHeader, Button, TextInput, TabBar, Field,
 * Toggle, Segmented.
 */
import { useEffect, useState } from "react";
import { Building2, Layers, Palette, Bot, ShieldCheck, Award } from "lucide-react";
import { usePureH } from "../context/PureHContext";
import { usePlatform } from "../context/PlatformContext";
import { Card, SectionHeader, Button, TextInput } from "../components/lib";
import { TabBar, Field, Toggle, Segmented } from "../components/lib.overlay";
import { api } from "../services/api";
import { useDataMode } from "../context/DataModeContext";
import { MarketBlocksEditor } from "../components/MarketBlocksEditor";
import { EmailSettingsCard } from "../components/EmailSettingsCard";

declare const process: { env: Record<string, string | undefined> };

type Tab = "company" | "brand" | "workspace" | "appearance" | "ai" | "email" | "security" | "samples" | "datamode";

/**
 * Some proxies / CDNs occasionally concat a keepalive frame or duplicate
 * response chunk after the JSON body. Chrome then throws:
 *   "Unexpected non-whitespace character after JSON at position N".
 * safeJson reads the body as text and — if native JSON.parse fails —
 * extracts the first well-balanced { … } object.
 */
async function safeJson(r: Response): Promise<any> {
  const text = await r.text();
  try { return JSON.parse(text); }
  catch { return safeJson._parseFirst(text); }
}
safeJson._parseFirst = (text: string): any => {
  const first = text.indexOf("{");
  if (first < 0) throw new Error(`Non-JSON response: ${text.slice(0, 200)}`);
  let depth = 0, inStr = false, esc = false, end = -1;
  for (let i = first; i < text.length; i++) {
    const c = text[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === "\\") esc = true;
      else if (c === '"') inStr = false;
    } else {
      if (c === '"') inStr = true;
      else if (c === "{") depth++;
      else if (c === "}") { depth--; if (depth === 0) { end = i + 1; break; } }
    }
  }
  if (end < 0) throw new Error(`Truncated JSON response: ${text.slice(0, 200)}`);
  return JSON.parse(text.slice(first, end));
};


export default function Settings() {
  const { currentCompany, currentWorkspace } = usePureH();
  const { prefs, setDensity } = usePlatform();
  const [tab, setTab] = useState<Tab>("company");

  const [companyName, setCompanyName] = useState(currentCompany?.name ?? "Pure H");
  const [workspaceName, setWorkspaceName] = useState(currentWorkspace?.name ?? "ORVAN Perfumes");
  const [currency, setCurrency] = useState(currentWorkspace?.currency ?? "EGP");
  const [industry, setIndustry] = useState(currentWorkspace?.industry ?? "Luxury Fragrance");

  const [aiAutoBrief, setAiAutoBrief] = useState(true);
  const [aiRecs, setAiRecs]           = useState(true);
  const [aiApprovals, setAiApprovals] = useState(false);
  const [twoFactor, setTwoFactor]     = useState(false);
  const [auditTrail, setAuditTrail]   = useState(true);
  const [ssoOnly, setSsoOnly]         = useState(false);

  // ── Real Company Profile (source of truth for PDFs) ──
  const [profile, setProfile] = useState<Record<string, string | number>>({});
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (tab !== "brand" || profileLoaded) return;
    api.companyProfile().then((p) => {
      setProfile(p as Record<string, string | number>);
      setProfileLoaded(true);
    }).catch(() => setProfileLoaded(true));
  }, [tab, profileLoaded]);

  async function saveBrand() {
    setProfileBusy(true);
    setProfileMsg(null);
    try {
      const patch: Record<string, unknown> = {};
      const keys = ["legalName","brandName","tagline","address","phone","email","website","tin","cr","vatPct","currency","invoiceTerms","quotationValidityDays","poDefaultLeadDays","logoText","logoDataUrl","logoName","logoMime","stampDataUrl","stampName","stampMime"];
      for (const k of keys) if (k in profile) patch[k] = profile[k];
      const updated = await api.patchCompanyProfile(patch);
      setProfile(updated as Record<string, string | number>);
      setProfileMsg({ ok: true, text: "Saved — every future PDF now reflects these values." });
    } catch (e) {
      setProfileMsg({ ok: false, text: (e as Error).message });
    } finally {
      setProfileBusy(false);
    }
  }
  function pset(k: string, v: string | number) {
    setProfile((cur) => ({ ...cur, [k]: v }));
  }

  return (
    <div data-testid="pureh-settings">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">Configure</div>
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Settings</h1>
        <div className="mt-1 text-sm text-slate-500">Company · Workspace · Appearance · AI · Security</div>
      </div>

      <div className="mb-6">
        <TabBar<Tab>
          testId="settings-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "company",    label: "Company"    },
            { key: "brand",      label: "Brand · PDF" },
            { key: "workspace",  label: "Workspace"  },
            { key: "appearance", label: "Appearance" },
            { key: "ai",         label: "AI"         },
            { key: "email",      label: "Email"      },
            { key: "security",   label: "Security"   },
            { key: "samples",    label: "Sample PDFs" },
            { key: "datamode",   label: "Data Mode"   },
          ]}
        />
      </div>

      {tab === "company" && (
        <Card testId="settings-tab-company">
          <SectionHeader eyebrow="Top level" title="Company" action={<Building2 size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Company name" inline>
            <div style={{ width: 260 }}><TextInput value={companyName} onChange={setCompanyName} testId="settings-company-name" /></div>
          </Field>
          <Field label="Kind" hint="Holding companies own one or more operating brands." inline>
            <Segmented<"holding" | "operating">
              testId="settings-company-kind"
              value={currentCompany?.kind ?? "holding"}
              onChange={() => { /* mock */ }}
              options={[
                { value: "holding",   label: "Holding"   },
                { value: "operating", label: "Operating" },
              ]}
            />
          </Field>
          <Field label="Active workspaces" inline>
            <span className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{currentCompany?.workspaceCount ?? 0}</span>
          </Field>
          <div className="pt-4 flex justify-end gap-2">
            <Button variant="secondary" testId="settings-company-cancel">Cancel</Button>
            <Button testId="settings-company-save">Save changes</Button>
          </div>
        </Card>
      )}

      {tab === "brand" && (
        <Card testId="settings-tab-brand">
          <SectionHeader
            eyebrow="Source of truth for every PDF"
            title="Brand & tax identity"
            action={<Award size={14} style={{ color: "var(--ph-ink-muted)" }} />}
          />
          <div className="text-xs mb-4" style={{ color: "var(--ph-ink-muted)" }}>
            Editing any field below immediately updates the header, footer, banking block, VAT rate and terms on every invoice, PO, expense voucher, quotation and report PDF generated from this workspace.
          </div>

          {/* Company logo — upload / preview / replace / remove */}
          <div
            className="mb-5 p-4"
            data-testid="settings-brand-logo-block"
            style={{
              border: "1px solid var(--ph-border-subtle)",
              borderRadius: "var(--ph-radius-sm)",
              background: "var(--ph-surface)",
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>
              Company Logo
            </div>
            <div className="flex items-center gap-4">
              <div
                data-testid="settings-brand-logo-preview"
                className="flex items-center justify-center"
                style={{
                  width: 180, height: 90,
                  border: "1px solid var(--ph-border-subtle)",
                  background: "var(--ph-surface-card)",
                  borderRadius: "var(--ph-radius-sm)",
                }}
              >
                {profile.logoDataUrl ? (
                  <img
                    src={String(profile.logoDataUrl)}
                    alt={String(profile.logoName ?? "logo")}
                    style={{ maxWidth: "160px", maxHeight: "70px", objectFit: "contain" }}
                    data-testid="settings-brand-logo-img"
                  />
                ) : (
                  <div
                    className="text-center"
                    style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 20, letterSpacing: 4, color: "var(--ph-gold, #B8965A)" }}
                    data-testid="settings-brand-logo-fallback"
                  >
                    {String(profile.logoText ?? "ORVAN")}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>
                  {profile.logoDataUrl ? (
                    <>Custom logo active — used on every generated PDF, HTML preview and printout.</>
                  ) : (
                    <>No logo uploaded. Documents will use the default text mark <b>{String(profile.logoText ?? "ORVAN")}</b>.</>
                  )}
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--ph-ink-muted)" }}>
                  PNG · JPG · SVG. Max 2 MB. Recommended 400 × 200 px transparent PNG.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <label
                    data-testid="settings-brand-logo-upload"
                    className="px-3 py-1.5 text-xs font-semibold uppercase cursor-pointer"
                    style={{
                      background: "var(--ph-gold, #B8965A)", color: "#fff",
                      letterSpacing: "0.14em", borderRadius: "var(--ph-radius-sm)",
                    }}
                  >
                    {profile.logoDataUrl ? "Replace logo" : "Upload logo"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      style={{ display: "none" }}
                      data-testid="settings-brand-logo-file"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const allowed = ["image/png","image/jpeg","image/svg+xml","image/webp"];
                        if (!allowed.includes(file.type)) {
                          setProfileMsg({ ok: false, text: "Unsupported file type. Please upload PNG, JPG, JPEG, SVG or WEBP." });
                          e.target.value = "";
                          return;
                        }
                        if (file.size > 2_000_000) {
                          setProfileMsg({ ok: false, text: "Logo must be under 2 MB." });
                          e.target.value = "";
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = async () => {
                          const dataUrl = String(reader.result || "");
                          try {
                            setProfileBusy(true);
                            await api.patchCompanyProfile({
                              logoDataUrl: dataUrl,
                              logoName:    file.name,
                              logoMime:    file.type,
                            });
                            // Re-fetch to guarantee we render the exact
                            // persisted profile (defensive against any
                            // partial-response caching).
                            const fresh = await api.companyProfile();
                            setProfile(fresh as Record<string, string | number>);
                            setProfileMsg({ ok: true, text: "Logo uploaded — every future PDF will use it." });
                          } catch (err) {
                            setProfileMsg({ ok: false, text: (err as Error).message });
                          } finally {
                            setProfileBusy(false);
                          }
                        };
                        reader.readAsDataURL(file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {profile.logoDataUrl && (
                    <button
                      type="button"
                      data-testid="settings-brand-logo-remove"
                      className="px-3 py-1.5 text-xs font-semibold uppercase"
                      style={{
                        border: "1px solid var(--ph-border-subtle)",
                        color: "var(--ph-danger)",
                        letterSpacing: "0.14em",
                        borderRadius: "var(--ph-radius-sm)",
                        background: "transparent",
                      }}
                      onClick={async () => {
                        try {
                          setProfileBusy(true);
                          await api.patchCompanyProfile({
                            logoDataUrl: "",
                            logoName:    "",
                            logoMime:    "",
                          });
                          const fresh = await api.companyProfile();
                          setProfile(fresh as Record<string, string | number>);
                          setProfileMsg({ ok: true, text: "Logo removed — documents will use the default text mark." });
                        } catch (err) {
                          setProfileMsg({ ok: false, text: (err as Error).message });
                        } finally {
                          setProfileBusy(false);
                        }
                      }}
                    >Remove logo</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Official company stamp — upload / preview / replace / remove */}
          <div
            className="mb-5 p-4"
            data-testid="settings-brand-stamp-block"
            style={{
              border: "1px solid var(--ph-border-subtle)",
              borderRadius: "var(--ph-radius-sm)",
              background: "var(--ph-surface)",
            }}
          >
            <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>
              Official Company Stamp
            </div>
            <div className="flex items-center gap-4">
              <div
                data-testid="settings-brand-stamp-preview"
                className="flex items-center justify-center"
                style={{
                  width: 180, height: 90,
                  border: "1px solid var(--ph-border-subtle)",
                  background: "var(--ph-surface-card)",
                  borderRadius: "var(--ph-radius-sm)",
                }}
              >
                {profile.stampDataUrl ? (
                  <img
                    src={String(profile.stampDataUrl)}
                    alt={String(profile.stampName ?? "stamp")}
                    style={{ maxWidth: "160px", maxHeight: "70px", objectFit: "contain" }}
                    data-testid="settings-brand-stamp-img"
                  />
                ) : (
                  <div
                    className="text-center"
                    style={{ fontSize: 11, color: "var(--ph-ink-muted)", letterSpacing: 2 }}
                    data-testid="settings-brand-stamp-fallback"
                  >
                    NO STAMP
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>
                  {profile.stampDataUrl ? (
                    <>Official stamp active — applied to every formal document (invoices, POs, expense vouchers, credit / debit notes, QC / batch / supplier profile / audit reports).</>
                  ) : (
                    <>No stamp uploaded. Formal documents will render without a stamp on the authorised-signature line.</>
                  )}
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--ph-ink-muted)" }}>
                  PNG · JPG · JPEG · SVG · WEBP. Max 2 MB. Recommended a transparent PNG so it blends with the signature line.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <label
                    data-testid="settings-brand-stamp-upload"
                    className="px-3 py-1.5 text-xs font-semibold uppercase cursor-pointer"
                    style={{
                      background: "var(--ph-gold, #B8965A)", color: "#fff",
                      letterSpacing: "0.14em", borderRadius: "var(--ph-radius-sm)",
                    }}
                  >
                    {profile.stampDataUrl ? "Replace stamp" : "Upload stamp"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      style={{ display: "none" }}
                      data-testid="settings-brand-stamp-file"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const allowed = ["image/png","image/jpeg","image/svg+xml","image/webp"];
                        if (!allowed.includes(file.type)) {
                          setProfileMsg({ ok: false, text: "Unsupported file type. Please upload PNG, JPG, JPEG, SVG or WEBP." });
                          e.target.value = "";
                          return;
                        }
                        if (file.size > 2_000_000) {
                          setProfileMsg({ ok: false, text: "Stamp must be under 2 MB." });
                          e.target.value = "";
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = async () => {
                          const dataUrl = String(reader.result || "");
                          try {
                            setProfileBusy(true);
                            const updated = await api.patchCompanyProfile({
                              stampDataUrl: dataUrl,
                              stampName:    file.name,
                              stampMime:    file.type,
                            });
                            // Re-fetch to guarantee we render the exact
                            // persisted profile (defensive against any
                            // partial-response caching).
                            const fresh = await api.companyProfile();
                            setProfile(fresh as Record<string, string | number>);
                            void updated;
                            setProfileMsg({ ok: true, text: "Official stamp uploaded — applied to every formal PDF." });
                          } catch (err) {
                            setProfileMsg({ ok: false, text: (err as Error).message });
                          } finally {
                            setProfileBusy(false);
                          }
                        };
                        reader.readAsDataURL(file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {profile.stampDataUrl && (
                    <button
                      type="button"
                      data-testid="settings-brand-stamp-remove"
                      className="px-3 py-1.5 text-xs font-semibold uppercase"
                      style={{
                        border: "1px solid var(--ph-border-subtle)",
                        color: "var(--ph-danger)",
                        letterSpacing: "0.14em",
                        borderRadius: "var(--ph-radius-sm)",
                        background: "transparent",
                      }}
                      onClick={async () => {
                        try {
                          setProfileBusy(true);
                          await api.patchCompanyProfile({
                            stampDataUrl: "",
                            stampName:    "",
                            stampMime:    "",
                          });
                          const fresh = await api.companyProfile();
                          setProfile(fresh as Record<string, string | number>);
                          setProfileMsg({ ok: true, text: "Stamp removed — formal documents will render without a stamp." });
                        } catch (err) {
                          setProfileMsg({ ok: false, text: (err as Error).message });
                        } finally {
                          setProfileBusy(false);
                        }
                      }}
                    >Remove stamp</button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Field label="Legal name" inline>
            <div style={{ width: 300 }}><TextInput value={String(profile.legalName ?? "")} onChange={(v) => pset("legalName", v)} testId="settings-brand-legal" /></div>
          </Field>
          <Field label="Brand name" inline>
            <div style={{ width: 300 }}><TextInput value={String(profile.brandName ?? "")} onChange={(v) => pset("brandName", v)} testId="settings-brand-name" /></div>
          </Field>
          <Field label="Tagline" inline>
            <div style={{ width: 380 }}><TextInput value={String(profile.tagline ?? "")} onChange={(v) => pset("tagline", v)} testId="settings-brand-tagline" /></div>
          </Field>
          <Field label="Address" inline>
            <div style={{ width: 380 }}><TextInput value={String(profile.address ?? "")} onChange={(v) => pset("address", v)} testId="settings-brand-address" /></div>
          </Field>
          <Field label="Phone" inline>
            <div style={{ width: 240 }}><TextInput value={String(profile.phone ?? "")} onChange={(v) => pset("phone", v)} testId="settings-brand-phone" /></div>
          </Field>
          <Field label="Email" inline>
            <div style={{ width: 300 }}><TextInput value={String(profile.email ?? "")} onChange={(v) => pset("email", v)} testId="settings-brand-email" /></div>
          </Field>
          <Field label="Website" inline>
            <div style={{ width: 300 }}><TextInput value={String(profile.website ?? "")} onChange={(v) => pset("website", v)} testId="settings-brand-web" /></div>
          </Field>
          <Field label="TIN (Tax ID)" inline>
            <div style={{ width: 240 }}><TextInput value={String(profile.tin ?? "")} onChange={(v) => pset("tin", v)} testId="settings-brand-tin" /></div>
          </Field>
          <Field label="Commercial Reg." inline>
            <div style={{ width: 240 }}><TextInput value={String(profile.cr ?? "")} onChange={(v) => pset("cr", v)} testId="settings-brand-cr" /></div>
          </Field>
          <Field label="VAT %" inline>
            <div style={{ width: 100 }}><TextInput value={String(profile.vatPct ?? "")} onChange={(v) => pset("vatPct", Number(v) || 0)} testId="settings-brand-vat" /></div>
          </Field>
          <Field label="Currency" inline>
            <div style={{ width: 100 }}><TextInput value={String(profile.currency ?? "")} onChange={(v) => pset("currency", v)} testId="settings-brand-currency" /></div>
          </Field>
          <Field label="Invoice terms" hint="Rendered inside the Terms & Conditions block of every invoice / quotation PDF.">
            <textarea
              data-testid="settings-brand-terms"
              value={String(profile.invoiceTerms ?? "")}
              onChange={(e) => pset("invoiceTerms", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm focus:outline-none"
              style={{
                color: "var(--ph-ink-strong)",
                background: "var(--ph-surface-card)",
                border: "1px solid var(--ph-border-strong)",
                borderRadius: "var(--ph-radius-sm)",
                fontFamily: "var(--ph-body-font)",
              }}
            />
          </Field>
          <Field label="Quotation validity (days)" inline>
            <div style={{ width: 120 }}><TextInput value={String(profile.quotationValidityDays ?? "")} onChange={(v) => pset("quotationValidityDays", Number(v) || 0)} testId="settings-brand-qtv" /></div>
          </Field>
          <Field label="Default PO lead time (days)" inline>
            <div style={{ width: 120 }}><TextInput value={String(profile.poDefaultLeadDays ?? "")} onChange={(v) => pset("poDefaultLeadDays", Number(v) || 0)} testId="settings-brand-lead" /></div>
          </Field>
          {profileMsg && (
            <div
              data-testid="settings-brand-msg"
              className="mt-3 p-3 text-sm"
              style={{
                background: profileMsg.ok ? "var(--ph-success-tint)" : "var(--ph-danger-tint)",
                color:      profileMsg.ok ? "var(--ph-success)"      : "var(--ph-danger)",
                borderRadius: "var(--ph-radius-sm)",
              }}
            >
              {profileMsg.text}
            </div>
          )}
          <div className="pt-4 flex justify-end gap-2">
            <Button variant="secondary" testId="settings-brand-cancel" onClick={() => { setProfileLoaded(false); setProfileMsg(null); }}>Reset</Button>
            <Button testId="settings-brand-save" onClick={saveBrand} disabled={profileBusy || !profileLoaded}>
              {profileBusy ? "Saving…" : "Save & propagate to all PDFs"}
            </Button>
          </div>
        </Card>
      )}

      {tab === "brand" && (
        <div className="mt-6">
          <MarketBlocksEditor />
        </div>
      )}

      {tab === "workspace" && (
        <Card testId="settings-tab-workspace">
          <SectionHeader eyebrow="Brand under company" title="Workspace" action={<Layers size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Workspace name" inline>
            <div style={{ width: 260 }}><TextInput value={workspaceName} onChange={setWorkspaceName} testId="settings-workspace-name" /></div>
          </Field>
          <Field label="Industry" inline>
            <div style={{ width: 260 }}><TextInput value={industry} onChange={setIndustry} testId="settings-workspace-industry" /></div>
          </Field>
          <Field label="Currency" hint="Used across dashboard KPIs and financial reports." inline>
            <div style={{ width: 120 }}><TextInput value={currency} onChange={setCurrency} testId="settings-workspace-currency" /></div>
          </Field>
          <div className="pt-4 flex justify-end gap-2">
            <Button variant="secondary" testId="settings-workspace-cancel">Cancel</Button>
            <Button testId="settings-workspace-save">Save changes</Button>
          </div>
        </Card>
      )}

      {tab === "appearance" && (
        <Card testId="settings-tab-appearance">
          <SectionHeader eyebrow="Visual DNA" title="Appearance" action={<Palette size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Design system" hint="Pure H Executive Warm v1 — frozen in Sprint 1." inline>
            <span
              className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase font-semibold"
              style={{
                letterSpacing: "0.14em",
                background: "var(--ph-brand-tint)",
                color: "var(--ph-brand-primary)",
                borderRadius: "var(--ph-radius-sm)",
              }}
            >
              Warm v1
            </span>
          </Field>
          <Field label="Density" hint="Applies globally across every screen." inline>
            <Segmented<typeof prefs.density>
              testId="settings-density"
              value={prefs.density}
              onChange={setDensity}
              options={[
                { value: "comfortable", label: "Comfortable" },
                { value: "compact",     label: "Compact"     },
              ]}
            />
          </Field>
          <Field label="Theme" hint="Dark & light variants ship in a later sprint." inline>
            <Segmented<"warm">
              testId="settings-theme"
              value="warm"
              onChange={() => { /* locked */ }}
              options={[{ value: "warm", label: "Executive Warm" }]}
            />
          </Field>
        </Card>
      )}

      {tab === "ai" && (
        <Card testId="settings-tab-ai">
          <SectionHeader eyebrow="AI teammates" title="AI" action={<Bot size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <Field label="Auto-generate Morning Brief" hint="Publishes to the Executive Dashboard every day at 06:00." inline>
            <Toggle checked={aiAutoBrief} onChange={setAiAutoBrief} testId="settings-ai-brief" />
          </Field>
          <Field label="Surface AI recommendations" hint="Discoveries and recommendations rendered inside the dashboard." inline>
            <Toggle checked={aiRecs} onChange={setAiRecs} testId="settings-ai-recs" />
          </Field>
          <Field label="Allow AI to request approvals" hint="AI teammates can create human-in-the-loop approval requests." inline>
            <Toggle checked={aiApprovals} onChange={setAiApprovals} testId="settings-ai-approvals" />
          </Field>
          <Field label="Model provider" hint="Managed centrally via the Emergent LLM key." inline>
            <span className="text-sm" style={{ color: "var(--ph-ink-strong)" }}>Emergent · Auto</span>
          </Field>
        </Card>
      )}

      {tab === "email" && <EmailSettingsCard />}

      {tab === "security" && (
        <Card testId="settings-tab-security">
          <SectionHeader eyebrow="Governance" title="Security" action={<ShieldCheck size={14} style={{ color: "var(--ph-ink-muted)" }} />} />

          <ChangePasswordForm />

          <div className="mt-6 pt-6 border-t border-slate-100">
            <Field label="Two-factor authentication" hint="Requires an authenticator app for every sign-in." inline>
              <Toggle checked={twoFactor} onChange={setTwoFactor} testId="settings-2fa" />
            </Field>
            <Field label="Audit trail" hint="Every action is logged and exportable." inline>
              <Toggle checked={auditTrail} onChange={setAuditTrail} testId="settings-audit" />
            </Field>
            <Field label="SSO only" hint="Disable password-based sign-in for the entire workspace." inline>
              <Toggle checked={ssoOnly} onChange={setSsoOnly} testId="settings-sso" />
            </Field>
            <Field label="Session lifetime" hint="How long a session stays valid before re-authentication is required." inline>
              <Segmented<"1h" | "12h" | "7d">
                testId="settings-session-ttl"
                value="12h"
                onChange={() => { /* mock */ }}
                options={[
                  { value: "1h",  label: "1 hour"  },
                  { value: "12h", label: "12 hours"},
                  { value: "7d",  label: "7 days"  },
                ]}
              />
            </Field>
          </div>
        </Card>
      )}
      {tab === "samples" && (
        <Card testId="settings-tab-samples">
          <SectionHeader
            eyebrow="Production Readiness"
            title="Sample PDFs for manual review"
            action={<Award size={14} style={{ color: "var(--ph-ink-muted)" }} />}
          />
          <div className="mb-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>
            Direct downloads of the 8 canonical single-record reports. Each card
            resolves its <em>first available record</em> at request time. When a
            collection is empty, the card shows an empty state — never a dead
            link to a fixture that no longer exists.
          </div>
          <SamplePdfsPanel />
          <div className="mt-4 text-xs" style={{ color: "var(--ph-ink-muted)" }}>
            Cookies flow automatically. If a download prompts for auth, sign back in and reopen this tab.
          </div>
        </Card>
      )}

      {tab === "datamode" && <DataModePanel />}
    </div>
  );
}

// ── Data Mode / Purge / Backup panel ────────────────────────────
function DataModePanel() {
  const API = `${(process as any).env.REACT_APP_BACKEND_URL}/api/pureh`;
  const { user } = usePureH();
  const { dataMode, updatedAt, updatedBy, setDataMode, refresh } = useDataMode();
  const isOwner = (user?.role || "").toLowerCase() === "owner" || Boolean((user as any)?.isHoldingOwner);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [backups, setBackups] = useState<Array<{ id: string; createdAt: string; totalRows: number; }>>([]);
  const [selectedBackup, setSelectedBackup] = useState<string>("");
  const [sandbox, setSandbox] = useState<{
    scratchDb: string;
    productionTouched: boolean;
    totals: { totalPurged: number; totalPreserved: number };
    emptied: Record<string, { before: number; after: number; deleted: number }>;
    survivors: Record<string, { live: number; scratch: number; sample: any }>;
  } | null>(null);
  const [lastBackup, setLastBackup] = useState<{
    id: string;
    totalRows: number;
    restoreVerified: boolean;
    contentVerified: boolean;
    countsVerified: boolean;
    scratchDbUsed: string;
    collectionsCovered?: number;
    counts: Record<string, number>;
    verifyCounts: Record<string, number>;
    sourceHashes: Record<string, string>;
    verifyHashes: Record<string, string>;
  } | null>(null);
  const [dryRunReport, setDryRunReport] = useState<{
    backupId: string;
    sampleCount: number;
    allMatch: boolean;
    results: Record<string, { expected: number; restored: number; match: boolean; hash: string }>;
  } | null>(null);
  const [audit, setAudit] = useState<{
    totals: { seed: number; real: number; structural: number };
    byCollection: Record<string, {
      total: number;
      counts: { seed: number; real: number; structural: number };
      examples: { seed: any[]; real: any[]; structural: any[] };
      recommendation: string;
    }>;
  } | null>(null);
  const [auditFilter, setAuditFilter] = useState<"purge_all" | "hybrid_needs_review" | "keep" | "all">("purge_all");
  const [scopedSelection, setScopedSelection] = useState<Record<string, "all" | "seed" | "keep"> | null>(null);
  const [scopedRationale, setScopedRationale] = useState<Record<string, string>>({});
  const [scopedReport, setScopedReport] = useState<any | null>(null);
  const [scopedFilter, setScopedFilter] = useState<"all" | "seed" | "keep" | "everything">("everything");

  const loadBackups = async () => {
    const r = await fetch(`${API}/system/backups`, { credentials: "include" });
    if (r.ok) setBackups(await r.json());
  };

  useEffect(() => { void loadBackups(); }, []);

  const runBackup = async () => {
    setBusy(true); setMsg(null); setErr(null); setLastBackup(null);
    try {
      const r = await fetch(`${API}/system/backup`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setLastBackup(d);
      setMsg(`Backup ${d.id} · ${d.totalRows} rows · counts verified: ${d.countsVerified ? "YES" : "NO"} · content-hash verified: ${d.contentVerified ? "YES" : "NO"} · restore verified: ${d.restoreVerified ? "YES ✓" : "NO ✗"}`);
      await loadBackups();
      setSelectedBackup(d.id);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  const runSandboxPurge = async () => {
    setBusy(true); setMsg(null); setErr(null); setSandbox(null);
    try {
      const r = await fetch(`${API}/system/purge-sandbox`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setSandbox(d);
      setMsg(`Sandbox purge preview complete · ${d.totals.totalPurged} rows would be deleted, ${d.totals.totalPreserved} rows would survive. Production DB untouched.`);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  const runAudit = async () => {
    setBusy(true); setMsg(null); setErr(null); setAudit(null);
    try {
      const r = await fetch(`${API}/system/data-audit`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setAudit(d);
      setMsg(`Audit complete · seed=${d.totals.seed} · real=${d.totals.real} · structural=${d.totals.structural}. Use this to decide the real purge scope.`);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  const runDryRunRestore = async () => {
    if (!lastBackup && !selectedBackup) { setErr("Take or select a backup first"); return; }
    const backupId = lastBackup?.id || selectedBackup;
    setBusy(true); setMsg(null); setErr(null); setDryRunReport(null);
    try {
      const r = await fetch(`${API}/system/restore-dry-run`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ backupId }),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setDryRunReport(d);
      setMsg(`Filesystem restore dry-run complete · sample=${d.sampleCount} · allMatch=${d.allMatch}`);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  const loadScopedDefault = async () => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await fetch(`${API}/system/purge-scoped-default`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setScopedSelection(d.selection);
      setScopedRationale(d.rationale);
      setMsg(`Loaded ${Object.keys(d.selection).length} collections with default policy: ${d.policy}`);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  const runScopedPurge = async (mode: "sandbox" | "live") => {
    if (!scopedSelection) { setErr("Load default selection first"); return; }
    if (mode === "live") {
      if (!selectedBackup) { setErr("Select a verified backup first."); return; }
      const nuked = Object.values(scopedSelection).filter(v => v === "all").length
                  + Object.values(scopedSelection).filter(v => v === "seed").length;
      if (!window.confirm(`This deletes rows across ${nuked} collections in the LIVE production DB. Reversible only via backup ${selectedBackup}. Continue?`)) return;
    }
    setBusy(true); setMsg(null); setErr(null); setScopedReport(null);
    try {
      const body: any = { mode, selection: scopedSelection };
      if (mode === "live" && selectedBackup) body.backupId = selectedBackup;
      const r = await fetch(`${API}/system/purge-scoped`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      let d: any;
      try { d = JSON.parse(text); }
      catch { d = safeJson._parseFirst(text); }
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setScopedReport(d);
      setMsg(`${mode === "sandbox" ? "Sandbox preview" : "LIVE purge"} complete · deleted=${d.totals.totalDeleted} · remaining=${d.totals.totalRemaining} · productionTouched=${d.productionTouched}`);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  // Legacy /system/purge-demo-data removed 2026-07-15. Use the scoped purge above.

  const restore = async () => {
    if (!selectedBackup) { setErr("Select a backup to restore."); return; }
    if (!window.confirm(`Restore backup ${selectedBackup}? This overwrites current business collections.`)) return;
    setBusy(true); setMsg(null); setErr(null);
    try {
      const r = await fetch(`${API}/system/restore`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ backupId: selectedBackup }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setMsg(`Restore complete · ${d.totalRows} rows restored across ${Object.keys(d.restored).length} collections.`);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  return (
    <Card testId="settings-tab-datamode">
      <SectionHeader eyebrow="Owner controls" title="Data Mode · Backup · Purge" />
      {!isOwner && (
        <div className="mt-4 p-3 rounded text-sm" data-testid="datamode-forbidden"
             style={{ background: "var(--ph-warning-tint, #fef3c7)", color: "#78350f",
                      border: "1px solid #b45309" }}>
          These controls are Owner-only. Sign in as an Owner (or Holding Owner) to view Data Mode, take backups, or purge demo data.
        </div>
      )}
      {isOwner && (
      <div className="mt-4 grid gap-4">
        <div className="p-3 rounded" style={{ background: "var(--ph-surface-well)", border: "1px solid var(--ph-border-subtle)" }} data-testid="datamode-current">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ph-ink-muted)" }}>Current mode</div>
          <div className="mt-1 text-2xl font-medium" style={{ color: dataMode === "demo" ? "#b45309" : "#059669" }}>
            {dataMode === "demo" ? "⚠ DEMO" : "✓ LIVE"}
          </div>
          <div className="mt-1 text-xs" style={{ color: "var(--ph-ink-muted)" }}>
            Updated {updatedAt || "—"} by {updatedBy || "—"}
          </div>
          <div className="mt-3 flex gap-2">
            <Button testId="datamode-set-demo" onClick={() => setDataMode("demo").then(refresh)}>Set DEMO</Button>
            <Button testId="datamode-set-live" onClick={() => setDataMode("live").then(refresh)}>Set LIVE</Button>
          </div>
        </div>

        <div className="p-3 rounded" style={{ background: "#eff6ff", border: "1px solid #3b82f6" }} data-testid="datamode-audit">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "#1e3a8a" }}>Step 0 · Data audit (start here)</div>
          <div className="mt-1 text-sm" style={{ color: "var(--ph-ink-default)" }}>Sweeps every collection and classifies each row as <b>seed</b> (fixture / demo / test artifact), <b>real</b> (created by users or business flows), or <b>structural</b> (workspace identity, CDM, backups). Read-only. This is what tells you the real purge scope.</div>
          <div className="mt-2"><Button testId="audit-run" disabled={busy} onClick={runAudit}>Run data audit</Button></div>
          {audit && (
            <div className="mt-3 p-2 rounded text-xs" data-testid="audit-report" style={{ background: "#dbeafe", border: "1px solid #60a5fa" }}>
              <div className="font-semibold mb-2">
                Totals · <span className="text-red-700">seed = {audit.totals.seed}</span>
                {" · "}<span className="text-emerald-700">real = {audit.totals.real}</span>
                {" · "}<span className="text-slate-700">structural = {audit.totals.structural}</span>
              </div>
              <div className="mb-2 flex gap-1">
                {(["purge_all","hybrid_needs_review","keep","all"] as const).map((f) => (
                  <button key={f}
                    data-testid={`audit-filter-${f}`}
                    onClick={() => setAuditFilter(f)}
                    className={`px-2 py-0.5 rounded text-[10px] ${auditFilter === f ? "bg-blue-900 text-white" : "bg-white text-blue-900 border border-blue-300"}`}>
                    {f === "purge_all" ? `Seed-only (${Object.values(audit.byCollection).filter(v => v.recommendation === "purge_all").length})`
                     : f === "hybrid_needs_review" ? `Mixed (${Object.values(audit.byCollection).filter(v => v.recommendation === "hybrid_needs_review").length})`
                     : f === "keep" ? `Keep (${Object.values(audit.byCollection).filter(v => v.recommendation === "keep").length})`
                     : `All (${Object.keys(audit.byCollection).length})`}
                  </button>
                ))}
              </div>
              <div className="font-mono text-[10px] max-h-96 overflow-y-auto space-y-1">
                {Object.entries(audit.byCollection)
                  .filter(([_, v]) => auditFilter === "all" || v.recommendation === auditFilter)
                  .filter(([_, v]) => v.total > 0)
                  .sort(([, a], [, b]) => (b.counts.seed - a.counts.seed))
                  .map(([coll, v]) => {
                    const badgeColor = v.recommendation === "purge_all" ? "text-red-700"
                                     : v.recommendation === "hybrid_needs_review" ? "text-amber-700"
                                     : "text-emerald-700";
                    const sample = v.examples.seed[0] || v.examples.real[0] || v.examples.structural[0];
                    return (
                      <details key={coll} data-testid={`audit-coll-${coll}`}>
                        <summary className={`cursor-pointer ${badgeColor}`}>
                          {coll.padEnd(38).slice(0, 38)} total={String(v.total).padStart(4)} · seed={v.counts.seed} · real={v.counts.real} · struct={v.counts.structural}
                          {sample && <span className="text-slate-500 ml-2">// {sample.label || sample.id}</span>}
                        </summary>
                        <div className="pl-4 mt-1 space-y-0.5">
                          {v.examples.seed.slice(0, 3).map((e: any, i: number) => (
                            <div key={`s-${i}`} className="text-red-700">SEED · id={e.id} · label={e.label} · why: {e.reason}</div>
                          ))}
                          {v.examples.real.slice(0, 3).map((e: any, i: number) => (
                            <div key={`r-${i}`} className="text-emerald-700">REAL · id={e.id} · label={e.label} · why: {e.reason}</div>
                          ))}
                          {v.examples.structural.slice(0, 3).map((e: any, i: number) => (
                            <div key={`x-${i}`} className="text-slate-700">STRUCTURAL · id={e.id} · label={e.label} · why: {e.reason}</div>
                          ))}
                        </div>
                      </details>
                    );
                  })
                }
              </div>
            </div>
          )}
        </div>

        <div className="p-3 rounded" style={{ background: "var(--ph-surface-well)", border: "1px solid var(--ph-border-subtle)" }} data-testid="datamode-backup">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ph-ink-muted)" }}>Step 1 · Take a verified backup</div>
          <div className="mt-1 text-sm" style={{ color: "var(--ph-ink-muted)" }}>Snapshot all business collections, restore into a scratch DB, then confirm counts AND content hashes match before you purge.</div>
          <div className="mt-2"><Button testId="backup-run" disabled={busy} onClick={runBackup}>Take & verify backup</Button></div>
          {lastBackup && (
            <div className="mt-3 p-2 rounded text-xs" data-testid="backup-verified-report"
                 style={{ background: "#ecfdf5", border: "1px solid #10b981", color: "#065f46" }}>
              <div className="font-semibold mb-1">Backup verification report · {lastBackup.id}</div>
              <div>Total rows: <span className="font-mono">{lastBackup.totalRows}</span> · Collections covered: <b>{lastBackup.collectionsCovered || Object.keys(lastBackup.counts).length}</b> · Scratch DB: <span className="font-mono">{lastBackup.scratchDbUsed}</span></div>
              <div>Counts match: <b>{lastBackup.countsVerified ? "YES" : "NO"}</b> · Content-hash match: <b>{lastBackup.contentVerified ? "YES" : "NO"}</b> · Restore verified: <b>{lastBackup.restoreVerified ? "YES ✓" : "NO ✗"}</b></div>
              <div className="mt-2 flex gap-2 items-center">
                <Button testId="restore-dry-run-run" disabled={busy} onClick={runDryRunRestore}>Prove restore against scratch DB (5-sample)</Button>
                <span className="text-[10px]">supplementary filesystem-level check</span>
              </div>
              {dryRunReport && (
                <div className="mt-2 p-2 rounded" data-testid="restore-dry-run-report"
                     style={{ background: "#f0fdf4", border: `1px solid ${dryRunReport.allMatch ? '#10b981' : '#dc2626'}` }}>
                  <div>Sample: <b>{dryRunReport.sampleCount}</b> collections · all match: <b>{dryRunReport.allMatch ? "YES ✓" : "NO ✗"}</b></div>
                  <div className="font-mono text-[10px] mt-1 space-y-0.5">
                    {Object.entries(dryRunReport.results).map(([coll, v]) => (
                      <div key={coll} className={v.match ? "text-emerald-700" : "text-red-700"}>
                        {v.match ? "✓" : "✗"} {coll}: expected={v.expected} restored={v.restored} hash={v.hash}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <details className="mt-1">
                <summary className="cursor-pointer">Per-collection hash equality ({Object.keys(lastBackup.counts).length} collections)</summary>
                <div className="mt-1 font-mono text-[10px] max-h-48 overflow-y-auto">
                  {Object.keys(lastBackup.counts).sort().map((c) => {
                    const src = lastBackup.sourceHashes[c];
                    const vfy = lastBackup.verifyHashes[c];
                    const ok  = src === vfy && lastBackup.counts[c] === lastBackup.verifyCounts[c];
                    return (
                      <div key={c} data-testid={`bkup-row-${c}`}>
                        {ok ? "✓" : "✗"} {c}: {lastBackup.counts[c]}→{lastBackup.verifyCounts[c]} · {src}→{vfy}
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          )}
          {backups.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ph-ink-muted)" }}>Existing backups</div>
              <select data-testid="backup-select" value={selectedBackup} onChange={(e) => setSelectedBackup(e.target.value)}
                      className="px-2 py-1 rounded border text-xs w-full max-w-md" style={{ borderColor: "var(--ph-border-subtle)" }}>
                <option value="">— select —</option>
                {backups.map((b) => (
                  <option key={b.id} value={b.id}>{b.id} · {b.createdAt} · {b.totalRows} rows</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="p-3 rounded" style={{ background: "#fef3c7", border: "1px solid #b45309" }} data-testid="datamode-sandbox">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "#78350f" }}>Step 1.5 · Sandbox purge preview (safe · scratch DB only)</div>
          <div className="mt-1 text-sm" style={{ color: "var(--ph-ink-default)" }}>Clones your production data into a temporary scratch DB, runs the purge <b>there</b>, then reports what would be emptied vs what would survive. Production is never touched.</div>
          <div className="mt-2"><Button testId="sandbox-purge-run" disabled={busy} onClick={runSandboxPurge}>Preview purge on a copy</Button></div>
          {sandbox && (
            <div className="mt-3 p-2 rounded text-xs" data-testid="sandbox-purge-report" style={{ background: "#fffbeb", border: "1px solid #f59e0b" }}>
              <div className="font-semibold mb-1">Sandbox: <span className="font-mono">{sandbox.scratchDb}</span></div>
              <div>Production touched: <b>{sandbox.productionTouched ? "YES ✗" : "NO ✓"}</b></div>
              <div>Would delete: <b>{sandbox.totals.totalPurged}</b> rows · Would preserve: <b>{sandbox.totals.totalPreserved}</b> rows</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-1">Emptied</div>
                  <div className="font-mono text-[10px] max-h-48 overflow-y-auto space-y-0.5">
                    {Object.entries(sandbox.emptied).filter(([_, v]) => v.before > 0).sort().map(([k, v]) => (
                      <div key={k} data-testid={`sbx-empty-${k}`} className="text-red-700">✗ {k}: {v.before} → {v.after}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Survivors</div>
                  <div className="font-mono text-[10px] max-h-48 overflow-y-auto space-y-0.5">
                    {Object.entries(sandbox.survivors).filter(([_, v]) => v.live > 0).sort().map(([k, v]) => (
                      <div key={k} data-testid={`sbx-surv-${k}`} className="text-emerald-700">✓ {k}: {v.live}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-3 rounded" style={{ background: "#f5f3ff", border: "1px solid #7c3aed" }} data-testid="datamode-scoped">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "#5b21b6" }}>Step 1.7 · Scoped purge (you choose the exact scope)</div>
          <div className="mt-1 text-sm" style={{ color: "var(--ph-ink-default)" }}>
            Per-collection selection: <b>all</b> = wipe every row; <b>seed</b> = delete only rows classified as seed (keeps hand-created real rows); <b>keep</b> = do not touch. Load the default policy then adjust individual collections. Preview in sandbox before hitting the live button.
          </div>
          <div className="mt-2 flex gap-2 flex-wrap">
            <Button testId="scoped-load-default" disabled={busy} onClick={loadScopedDefault}>Load recommended selection</Button>
            <Button testId="scoped-preview-sandbox" disabled={busy || !scopedSelection} onClick={() => runScopedPurge("sandbox")}>Preview in sandbox</Button>
            <Button testId="scoped-run-live" disabled={busy || !scopedSelection || !selectedBackup} onClick={() => runScopedPurge("live")}>Run LIVE (requires backup)</Button>
          </div>

          {scopedSelection && (
            <div className="mt-3 p-2 rounded text-xs" data-testid="scoped-selection" style={{ background: "#ede9fe", border: "1px solid #a78bfa" }}>
              <div className="font-semibold mb-1">
                Selection: <span className="text-red-700">all = {Object.values(scopedSelection).filter(v => v === "all").length}</span>
                {" · "}<span className="text-amber-700">seed = {Object.values(scopedSelection).filter(v => v === "seed").length}</span>
                {" · "}<span className="text-emerald-700">keep = {Object.values(scopedSelection).filter(v => v === "keep").length}</span>
              </div>
              <div className="mb-2 flex gap-1 flex-wrap">
                {(["everything","all","seed","keep"] as const).map((f) => (
                  <button key={f}
                    data-testid={`scoped-filter-${f}`}
                    onClick={() => setScopedFilter(f)}
                    className={`px-2 py-0.5 rounded text-[10px] ${scopedFilter === f ? "bg-purple-900 text-white" : "bg-white text-purple-900 border border-purple-300"}`}>
                    {f === "everything" ? `All (${Object.keys(scopedSelection).length})`
                     : f === "all"  ? `→ all (${Object.values(scopedSelection).filter(v => v === "all").length})`
                     : f === "seed" ? `→ seed (${Object.values(scopedSelection).filter(v => v === "seed").length})`
                     : `→ keep (${Object.values(scopedSelection).filter(v => v === "keep").length})`}
                  </button>
                ))}
              </div>
              <div className="font-mono text-[10px] max-h-96 overflow-y-auto space-y-0.5">
                {Object.entries(scopedSelection)
                  .filter(([_, v]) => scopedFilter === "everything" || v === scopedFilter)
                  .sort()
                  .map(([coll, action]) => (
                    <div key={coll} data-testid={`scoped-row-${coll}`} className="flex items-center gap-2">
                      <span className="w-72 truncate">{coll}</span>
                      <select
                        data-testid={`scoped-action-${coll}`}
                        value={action}
                        onChange={(e) => setScopedSelection({ ...scopedSelection, [coll]: e.target.value as any })}
                        className={`px-1 py-0.5 rounded border text-[10px] ${
                          action === "all"  ? "bg-red-100 text-red-800 border-red-300" :
                          action === "seed" ? "bg-amber-100 text-amber-800 border-amber-300" :
                                              "bg-emerald-100 text-emerald-800 border-emerald-300"}`}
                      >
                        <option value="all">all</option>
                        <option value="seed">seed</option>
                        <option value="keep">keep</option>
                      </select>
                      <span className="text-slate-500 flex-1 truncate">{scopedRationale[coll] || ""}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {scopedReport && (
            <div className="mt-3 p-2 rounded text-xs" data-testid="scoped-report"
                 style={{ background: scopedReport.productionTouched ? "#fecaca" : "#d1fae5",
                          border: `1px solid ${scopedReport.productionTouched ? "#dc2626" : "#059669"}` }}>
              <div className="font-semibold mb-1">
                {scopedReport.mode === "sandbox" ? "SANDBOX PREVIEW" : "LIVE PURGE COMPLETE"} · scratchDb={scopedReport.scratchDb || "n/a"}
              </div>
              <div>Production touched: <b>{scopedReport.productionTouched ? "YES" : "NO ✓"}</b></div>
              <div>Deleted: <b>{scopedReport.totals.totalDeleted}</b> · Remaining: <b>{scopedReport.totals.totalRemaining}</b> · Collections purged: <b>{scopedReport.totals.collectionsPurged}</b></div>
              <details className="mt-1">
                <summary className="cursor-pointer">Per-collection outcome</summary>
                <div className="font-mono text-[10px] max-h-64 overflow-y-auto mt-1 space-y-0.5">
                  {Object.entries(scopedReport.report as Record<string, any>)
                    .filter(([_, v]) => v.deleted > 0 || v.before > 0)
                    .sort(([, a], [, b]) => (b.deleted - a.deleted))
                    .map(([coll, v]) => (
                      <div key={coll} className={
                        v.deleted > 0 ? "text-red-700" :
                        v.action === "keep" && v.before > 0 ? "text-emerald-700" : "text-slate-500"
                      }>
                        {v.action === "keep" ? "KEEP" : v.deleted > 0 ? "DEL " : "SKIP"} · {coll.padEnd(38).slice(0,38)} {v.before} → {v.after} (-{v.deleted})
                      </div>
                    ))
                  }
                </div>
              </details>
            </div>
          )}
        </div>

        <div className="p-3 rounded" style={{ background: "var(--ph-surface-well)", border: "1px solid var(--ph-border-subtle)" }} data-testid="datamode-restore">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ph-ink-muted)" }}>Restore</div>
          <div className="mt-1 text-sm" style={{ color: "var(--ph-ink-muted)" }}>Restore the workspace state from a selected verified backup. Overwrites the current DB.</div>
          <div className="mt-2 flex gap-2">
            <Button testId="restore-run" disabled={busy || !selectedBackup} onClick={restore}>Restore selected backup</Button>
          </div>
        </div>

        {(msg || err) && (
          <div className="p-3 rounded text-sm" data-testid="datamode-message"
               style={{ background: err ? "var(--ph-danger-tint)" : "var(--ph-success-tint)",
                        color: err ? "var(--ph-danger)" : "var(--ph-success)",
                        border: `1px solid ${err ? "var(--ph-danger)" : "var(--ph-success)"}` }}>
            {err || msg}
          </div>
        )}
      </div>
      )}
    </Card>
  );
}


// ── Change Password Form ────────────────────────────────────────
// Rotates the Pure H owner's password via
// POST /api/pureh/auth/change-password. Namespaced under /api/pureh/
// so this cannot accidentally hit the legacy Hassan OS endpoint
// (which is a distinct auth scope operating on `db.users`).
function ChangePasswordForm() {
  const [oldPw, setOldPw]           = useState("");
  const [newPw, setNewPw]           = useState("");
  const [confirmPw, setConfirmPw]   = useState("");
  const [busy, setBusy]             = useState(false);
  const [msg, setMsg]               = useState<string | null>(null);
  const [err, setErr]               = useState<string | null>(null);

  // Client-side leaked-password blocklist. Mirrors the server-side
  // LEAKED_PASSWORDS set. Kept in code (not fetched from the API) so
  // typing a listed value shows a warning without a round trip. The
  // server is still the authority — anything that slips past this
  // list is rejected server-side.
  const LEAKED_PASSWORDS: ReadonlySet<string> = new Set([
    "Hassan@2026", "Yassin@1234", "Mezo@2030",
    "Anas@2030",   "Phase5@2026",
    "password", "12345678", "admin", "changeme",
    "welcome1", "hassan", "orvan",
  ]);

  // Client-side policy — mirrors the backend rule exactly so the user
  // never sees a 400 for something we could have caught. Backend is the
  // authority; this is UX guidance.
  function clientPolicyError(): string | null {
    if (!oldPw)                                     return "Current password is required.";
    if (newPw.length < 12)                          return "New password must be at least 12 characters.";
    if (!/[A-Za-z]/.test(newPw))                    return "New password must contain at least one letter.";
    if (!/\d/.test(newPw))                          return "New password must contain at least one digit.";
    if (newPw === oldPw)                            return "New password must differ from your current password.";
    if (LEAKED_PASSWORDS.has(newPw))                return "That password is on the workspace leaked-password list — pick another.";
    if (newPw !== confirmPw)                        return "New password and confirmation do not match.";
    return null;
  }

  const gate = clientPolicyError();

  async function submit() {
    setMsg(null); setErr(null);
    const policy = clientPolicyError();
    if (policy) { setErr(policy); return; }

    setBusy(true);
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/pureh";
      const r = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ oldPassword: oldPw, newPassword: newPw }),
      });
      const data = await safeJson(r).catch(() => ({} as any));
      if (!r.ok) {
        setErr(data?.detail || `Password change failed (HTTP ${r.status}).`);
      } else if (data?.scope !== "pureh") {
        setErr("Password rotated, but the server did not confirm the Pure H scope. Contact support before relying on the rotation.");
      } else {
        setMsg("Password updated. Other active sessions were signed out. Your session on this device stays active.");
        setOldPw(""); setNewPw(""); setConfirmPw("");
      }
    } catch (e: any) {
      setErr(e?.message || "Network error while rotating password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="change-password-form" className="space-y-4">
      <div>
        <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>Change password</div>
        <div className="text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
          Minimum 12 characters · letter + digit required · rotates <span className="tabular-nums">Pure H</span> scope only ·
          all other active sessions on this workspace will be signed out.
        </div>
      </div>

      <Field label="Current password" hint="The password you use to sign in today." inline>
        <input
          data-testid="pw-current"
          type="password"
          autoComplete="current-password"
          className="w-full max-w-sm px-3 py-2 text-sm border rounded"
          style={{ borderColor: "var(--ph-border)" }}
          value={oldPw}
          onChange={(e) => { setOldPw(e.target.value); setErr(null); setMsg(null); }}
          disabled={busy}
        />
      </Field>

      <Field label="New password" hint="12+ chars, at least one letter and one digit, different from your current." inline>
        <input
          data-testid="pw-new"
          type="password"
          autoComplete="new-password"
          className="w-full max-w-sm px-3 py-2 text-sm border rounded"
          style={{ borderColor: "var(--ph-border)" }}
          value={newPw}
          onChange={(e) => { setNewPw(e.target.value); setErr(null); setMsg(null); }}
          disabled={busy}
        />
      </Field>

      <Field label="Confirm new password" hint="Type the new password again." inline>
        <input
          data-testid="pw-confirm"
          type="password"
          autoComplete="new-password"
          className="w-full max-w-sm px-3 py-2 text-sm border rounded"
          style={{ borderColor: "var(--ph-border)" }}
          value={confirmPw}
          onChange={(e) => { setConfirmPw(e.target.value); setErr(null); setMsg(null); }}
          disabled={busy}
        />
      </Field>

      {gate && (oldPw || newPw || confirmPw) && (
        <div className="text-[11px]" data-testid="pw-policy-hint" style={{ color: "var(--ph-ink-muted)" }}>
          {gate}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          testId="pw-submit"
          onClick={submit}
          disabled={busy || !!gate}
          variant="primary"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>

        {err && (
          <div className="text-xs" data-testid="pw-error" style={{ color: "var(--ph-danger)" }}>
            {err}
          </div>
        )}
        {msg && (
          <div className="text-xs" data-testid="pw-success" style={{ color: "var(--ph-success)" }}>
            {msg}
          </div>
        )}
      </div>
    </div>
  );
}


// ── Sample PDFs panel ───────────────────────────────────────────
// For each of the 8 canonical doc keys, ask the backend for the
// FIRST available record id at mount time. If the backing collection
// is empty, the card renders an empty state with the download button
// disabled — never a dead link to a fixture that no longer exists.
type SampleRow = {
  key: string;
  label: string;
  emptyRef: string;
  liveRefLabel: string;
};
const SAMPLE_ROWS: readonly SampleRow[] = [
  { key: "customer-invoice",   label: "Customer Invoice",   emptyRef: "No invoices yet",         liveRefLabel: "First invoice" },
  { key: "expense-voucher",    label: "Expense Voucher",    emptyRef: "No expenses yet",         liveRefLabel: "First expense" },
  { key: "purchase-order",     label: "Purchase Order",     emptyRef: "No purchase orders yet",  liveRefLabel: "First purchase order" },
  { key: "purchase-request",   label: "Purchase Request",   emptyRef: "No purchase requests yet",liveRefLabel: "First purchase request" },
  { key: "lab-session-report", label: "Lab Session Report", emptyRef: "No lab sessions yet",     liveRefLabel: "First lab session" },
  { key: "blind-panel-report", label: "Blind Panel Report", emptyRef: "No blind panels yet",     liveRefLabel: "First blind panel" },
  { key: "batch-report",       label: "Batch Report",       emptyRef: "No batches yet",          liveRefLabel: "First production batch" },
  { key: "qc-report",          label: "QC Report",          emptyRef: "No QC records yet",       liveRefLabel: "First QC record" },
];

function SamplePdfsPanel() {
  const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/pureh";
  const [state, setState] = useState<Record<string, { loading: boolean; recordId: string | null; found: boolean; err?: string }>>(
    () => Object.fromEntries(SAMPLE_ROWS.map(r => [r.key, { loading: true, recordId: null, found: false }])),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all(SAMPLE_ROWS.map(async (r) => {
        try {
          const res = await fetch(`${API}/documents/single/${r.key}/first`, { credentials: "include" });
          const data = await safeJson(res).catch(() => ({} as any));
          if (cancelled) return;
          if (!res.ok) {
            setState(prev => ({ ...prev, [r.key]: { loading: false, recordId: null, found: false, err: data?.detail || `HTTP ${res.status}` } }));
            return;
          }
          setState(prev => ({ ...prev, [r.key]: { loading: false, recordId: data?.recordId || null, found: Boolean(data?.found) } }));
        } catch (e: any) {
          if (cancelled) return;
          setState(prev => ({ ...prev, [r.key]: { loading: false, recordId: null, found: false, err: e?.message || "network error" } }));
        }
      }));
    })();
    return () => { cancelled = true; };
  }, [API]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="samples-panel">
      {SAMPLE_ROWS.map((row) => {
        const s = state[row.key];
        const enabled = s && !s.loading && s.found && Boolean(s.recordId);
        const base    = process.env.REACT_APP_BACKEND_URL || "";
        const pdfHref     = enabled ? `${base}/api/pureh/documents/single/${row.key}/${encodeURIComponent(s.recordId!)}/pdf`     : undefined;
        const previewHref = enabled ? `${base}/api/pureh/documents/single/${row.key}/${encodeURIComponent(s.recordId!)}/preview` : undefined;
        const refLine: string =
          s?.loading           ? "Resolving first record…" :
          s?.err               ? `Lookup error: ${s.err}` :
          !s?.found            ? row.emptyRef :
          `${row.liveRefLabel} · ${s.recordId}`;
        return (
          <div
            key={row.key}
            data-testid={`samples-row-${row.key}`}
            data-empty={!enabled ? "true" : "false"}
            className="p-4 flex items-start justify-between gap-3"
            style={{ border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-sm)" }}
          >
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>
                {row.key}
              </div>
              <div className="mt-1 font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{row.label}</div>
              <div className="mt-0.5 text-xs truncate" style={{ color: "var(--ph-ink-muted)" }}>{refLine}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {enabled ? (
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`samples-pdf-${row.key}`}
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]"
                  style={{ background: "var(--ph-gold, #B8965A)", color: "#fff", borderRadius: "var(--ph-radius-sm)", letterSpacing: "0.14em" }}
                >Download PDF</a>
              ) : (
                <button
                  type="button"
                  disabled
                  data-testid={`samples-pdf-${row.key}`}
                  data-disabled="true"
                  aria-disabled="true"
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] cursor-not-allowed"
                  style={{ background: "var(--ph-border-subtle)", color: "var(--ph-ink-muted)", borderRadius: "var(--ph-radius-sm)", letterSpacing: "0.14em" }}
                  title={s?.err || row.emptyRef}
                >Download PDF</button>
              )}
              {enabled ? (
                <a
                  href={previewHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`samples-preview-${row.key}`}
                  className="px-3 py-1.5 text-xs uppercase tracking-[0.14em]"
                  style={{ border: "1px solid var(--ph-border-subtle)", color: "var(--ph-ink-default)", borderRadius: "var(--ph-radius-sm)", letterSpacing: "0.14em" }}
                >Preview HTML</a>
              ) : (
                <button
                  type="button"
                  disabled
                  data-testid={`samples-preview-${row.key}`}
                  data-disabled="true"
                  aria-disabled="true"
                  className="px-3 py-1.5 text-xs uppercase tracking-[0.14em] cursor-not-allowed"
                  style={{ border: "1px solid var(--ph-border-subtle)", color: "var(--ph-ink-muted)", borderRadius: "var(--ph-radius-sm)", letterSpacing: "0.14em" }}
                >Preview HTML</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
